import {
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import type {
    FindingPlanumDocType,
    FindingProfileDocType,
    FindingType
} from '@ast-data/shared';
import { useProjectDescriptions } from '../findings/useFamilyDescriptions';
import {
    DataGridPro,
    naturalSortComparator,
    useGridApiRef,
    type GridColDef,
    type GridFilterModel,
    type GridListViewColDef,
    type GridRowHeightParams,
    type GridSortModel
} from '../../lib/dataGrid';
import SearchField from '../../components/SearchField';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useFindingDuplicates } from '../findings/useFindingDuplicates';
import FieldFindingCard from './FieldFindingCard';
import FieldSortButton from './FieldSortButton';
import { useFieldListSort, type FieldSortOption } from './fieldListSort';
import {
    loadFindingsListState,
    saveFindingsListState
} from './fieldFindingsListState';

/** One Befund card's row in the grid. */
interface FieldFindingRow {
    id: string;
    findingNumber: string;
    findingType: FindingType;
    shortDescription: string;
    interpretation: string;
    preliminaryDating: string;
    planums: FindingPlanumDocType[];
    profiles: FindingProfileDocType[];
    isDuplicate: boolean;
    /** The latest change to the Befund or any of its descriptions. */
    updatedAt: string;
}

/**
 * Shared by every Befund without descriptions, so the card's `memo` sees the
 * same props on a re-render instead of two fresh `[]` literals.
 */
const NO_PLANUMS: FindingPlanumDocType[] = [];
const NO_PROFILES: FindingProfileDocType[] = [];

/**
 * Never displayed — the grid renders one column, the card (`listViewColumn`).
 * These exist so the quick filter has values to match and the list has its
 * natural Befundnummer order.
 */
const COLUMNS: GridColDef<FieldFindingRow>[] = [
    { field: 'findingNumber', sortComparator: naturalSortComparator },
    { field: 'shortDescription' },
    { field: 'updatedAt' }
];

/** Keyed by the columns above, so a sort option names its column. */
const SORT_OPTIONS: FieldSortOption[] = [
    { key: 'findingNumber', labelId: 'field.sort.findingNumber' },
    { key: 'updatedAt', labelId: 'field.sort.updatedAt', direction: 'desc' }
];

/** The newest of `since` and these ISO timestamps. */
function latest(
    stamps: readonly { updatedAt: string }[],
    since: string
): string {
    return stamps.reduce(
        (newest, { updatedAt }) => (updatedAt > newest ? updatedAt : newest),
        since
    );
}

const AUTO_ROW_HEIGHT = () => 'auto' as const;

/**
 * Rough card geometry in px, for `getEstimatedRowHeight` alone: a row's real
 * height is measured once it renders. Being close is what stops the scrollbar
 * thumb jumping around as a long list scrolls.
 */
const CARD_HEADER_HEIGHT = 76;
const SUBTITLE_HEIGHT = 20;
const CARD_CHROME_HEIGHT = 57;
const BLOCK_HEADER_HEIGHT = 32;
const BLOCK_GAP = 16;
const DESCRIPTION_ROW_HEIGHT = 36;
const EMPTY_BLOCK_HEIGHT = 20;
/** Vertical gap between cards, applied as the grid cell's bottom padding. */
const CARD_GAP = 16;

function estimateBlockHeight(count: number): number {
    return (
        BLOCK_HEADER_HEIGHT +
        (count > 0 ? count * DESCRIPTION_ROW_HEIGHT : EMPTY_BLOCK_HEIGHT)
    );
}

function estimateCardHeight(
    row: FieldFindingRow,
    descriptionsSideBySide: boolean
): number {
    const planumBlock = estimateBlockHeight(row.planums.length);
    const profileBlock = estimateBlockHeight(row.profiles.length);
    return (
        CARD_HEADER_HEIGHT +
        (row.interpretation || row.preliminaryDating ? SUBTITLE_HEIGHT : 0) +
        CARD_CHROME_HEIGHT +
        // The two description blocks sit side by side from `sm` up and stacked
        // below it (see FindingDescriptionList's grid).
        (descriptionsSideBySide
            ? Math.max(planumBlock, profileBlock)
            : planumBlock + BLOCK_GAP + profileBlock) +
        CARD_GAP
    );
}

/** How often a scroll may be written to storage, in ms. */
const SCROLL_SAVE_INTERVAL = 250;

/**
 * Touch-first list of a project's findings, with fast add. The project is named
 * in the app bar, so it is not repeated as a heading.
 *
 * The cards are rows of a list-view DataGrid rather than a plain stack: a
 * project can hold hundreds of Befunde and only the grid's row virtualisation
 * keeps that off the DOM. The grid also owns sorting and search, so neither
 * costs an array copy per keystroke.
 */
export default function FieldFindingsPage() {
    const intl = useIntl();
    const theme = useTheme();
    const navigate = useNavigate();
    const apiRef = useGridApiRef();
    const { projectId = '' } = useParams<{ projectId: string }>();
    const descriptionsSideBySide = useMediaQuery(theme.breakpoints.up('sm'));

    const [search, setSearch] = useState(
        () => loadFindingsListState(projectId).search
    );
    // Mirrored out of the grid so it can be saved without re-rendering the page
    // on every scroll frame. Tagged with the project it was measured in:
    // switching project leaves the last offset behind, and it must not be
    // written under the new project's key.
    const scrollTop = useRef({ projectId, top: 0 });
    // The project whose saved offset has already been jumped to. Keyed by
    // project rather than a flag, so switching project re-arms it by itself.
    const restoredScroll = useRef<string | null>(null);

    // Switching project keeps this component mounted (same route, new param),
    // so the remembered state is picked up here rather than in an effect: an
    // effect would save the outgoing search under the incoming project's key.
    const [shownProjectId, setShownProjectId] = useState(projectId);
    const [addAnchor, setAddAnchor] = useState<null | HTMLElement>(null);
    if (shownProjectId !== projectId) {
        setShownProjectId(projectId);
        setSearch(loadFindingsListState(projectId).search);
    }

    // One subscription for the whole project's Befunde and their descriptions,
    // grouped by Befund below — cheaper than a query per card.
    const { findings, planums, profiles } = useProjectDescriptions(projectId);

    const planumsByFinding = useMemo(() => {
        const map = new Map<string, FindingPlanumDocType[]>();
        for (const planum of planums) {
            const list = map.get(planum.findingId);
            if (list) {
                list.push(planum);
            } else {
                map.set(planum.findingId, [planum]);
            }
        }
        return map;
    }, [planums]);
    const profilesByFinding = useMemo(() => {
        const map = new Map<string, FindingProfileDocType[]>();
        for (const profile of profiles) {
            const list = map.get(profile.findingId);
            if (list) {
                list.push(profile);
            } else {
                map.set(profile.findingId, [profile]);
            }
        }
        return map;
    }, [profiles]);

    const ability = useAbility();
    const canCreate = ability.can(
        'create',
        scopedSubject('Finding', { projectId })
    );
    const duplicates = useFindingDuplicates(projectId);

    const rows = useMemo<FieldFindingRow[]>(
        () =>
            findings.map((finding) => ({
                id: finding.id,
                findingNumber: finding.findingNumber,
                findingType: finding.findingType,
                shortDescription: finding.shortDescription ?? '',
                interpretation: finding.interpretation ?? '',
                preliminaryDating: finding.preliminaryDating ?? '',
                planums: planumsByFinding.get(finding.id) ?? NO_PLANUMS,
                profiles: profilesByFinding.get(finding.id) ?? NO_PROFILES,
                isDuplicate: duplicates.has(finding.id),
                // Working on a Planum is working on its Befund: it moves the
                // card up under "zuletzt geändert" too.
                updatedAt: latest(
                    [
                        ...(planumsByFinding.get(finding.id) ?? NO_PLANUMS),
                        ...(profilesByFinding.get(finding.id) ?? NO_PROFILES)
                    ],
                    finding.updatedAt
                )
            })),
        [findings, planumsByFinding, profilesByFinding, duplicates]
    );

    const [listSort, setListSort] = useFieldListSort('findings', SORT_OPTIONS);
    const sortModel = useMemo<GridSortModel>(
        () => [
            { field: listSort.key, sort: listSort.direction },
            // Equal timestamps keep the Befundnummer order.
            ...(listSort.key === 'findingNumber'
                ? []
                : [{ field: 'findingNumber', sort: 'asc' as const }])
        ],
        [listSort.key, listSort.direction]
    );

    // Deferred so a keystroke paints immediately and the grid re-filters
    // behind it, instead of the two competing for the same frame.
    const deferredSearch = useDeferredValue(search);
    const filterModel = useMemo<GridFilterModel>(
        () => ({
            items: [],
            quickFilterValues: deferredSearch
                .trim()
                .split(/\s+/)
                .filter(Boolean)
        }),
        [deferredSearch]
    );

    const listViewColumn = useMemo<GridListViewColDef<FieldFindingRow>>(
        () => ({
            field: 'card',
            renderCell: ({ row }) => (
                <FieldFindingCard
                    projectId={projectId}
                    findingId={row.id}
                    findingNumber={row.findingNumber}
                    findingType={row.findingType}
                    shortDescription={row.shortDescription}
                    interpretation={row.interpretation}
                    preliminaryDating={row.preliminaryDating}
                    planums={row.planums}
                    profiles={row.profiles}
                    isDuplicate={row.isDuplicate}
                />
            )
        }),
        [projectId]
    );

    const getEstimatedRowHeight = useCallback(
        ({ model }: GridRowHeightParams) =>
            estimateCardHeight(
                model as FieldFindingRow,
                descriptionsSideBySide
            ),
        [descriptionsSideBySide]
    );

    const hasSearch = deferredSearch.trim() !== '';
    const slots = useMemo(
        () => ({
            noRowsOverlay: () => (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        p: 2,
                        textAlign: 'center'
                    }}
                >
                    <Typography sx={{ color: 'text.secondary' }}>
                        <FormattedMessage
                            id={
                                hasSearch
                                    ? 'field.findings.noMatches'
                                    : 'field.findings.empty'
                            }
                        />
                    </Typography>
                </Box>
            )
        }),
        [hasSearch]
    );

    /**
     * Remember the scroll offset: throttled while scrolling (each write
     * serialises the store) and flushed when the list goes away.
     */
    useEffect(() => {
        const api = apiRef.current;
        if (!api) {
            return;
        }
        let lastSaved = 0;
        const unsubscribe = api.subscribeEvent(
            'scrollPositionChange',
            (params) => {
                scrollTop.current = { projectId, top: params.top };
                const now = Date.now();
                if (now - lastSaved >= SCROLL_SAVE_INTERVAL) {
                    lastSaved = now;
                    saveFindingsListState(projectId, { scrollTop: params.top });
                }
            }
        );
        return () => {
            unsubscribe();
            if (scrollTop.current.projectId === projectId) {
                saveFindingsListState(projectId, {
                    scrollTop: scrollTop.current.top
                });
            }
        };
    }, [apiRef, projectId]);

    /**
     * …and go back to it once there are rows to scroll through; after paint, so
     * the grid sizes its viewport first. Rows are measured lazily, so a deep
     * offset lands close to rather than exactly on its card.
     */
    useEffect(() => {
        if (restoredScroll.current === projectId || rows.length === 0) {
            return;
        }
        restoredScroll.current = projectId;
        const top = loadFindingsListState(projectId).scrollTop;
        if (top <= 0) {
            return;
        }
        const frame = requestAnimationFrame(() => {
            apiRef.current?.scroll({ top });
        });
        return () => cancelAnimationFrame(frame);
    }, [apiRef, projectId, rows.length]);

    function handleSearchChange(value: string) {
        setSearch(value);
        // Persisted here rather than in an effect, so switching project can't
        // write the outgoing term under the incoming project's key.
        saveFindingsListState(projectId, { search: value });
    }

    // Adding a Befund asks WHICH KIND first: the type decides what its
    // descriptions ask for and is settled at creation (see
    // FINDING_TYPE_OPTIONS), so it cannot wait for the form.
    function handleAdd(type?: 'structure') {
        setAddAnchor(null);
        // The office dialog inserts on save (mode="new") — no draft up front.
        navigate(
            `/field/${projectId}/findings/new${type ? `?type=${type}` : ''}`
        );
    }

    return (
        <Box
            sx={{
                // Claim the height FieldLayout offers, so the grid scrolls
                // internally instead of growing the page.
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Stack spacing={2} sx={{ pb: 2 }}>
                {canCreate && (
                    <>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={(event) =>
                                setAddAnchor(event.currentTarget)
                            }
                        >
                            <FormattedMessage id="finding.add" />
                        </Button>
                        <Menu
                            anchorEl={addAnchor}
                            open={Boolean(addAnchor)}
                            onClose={() => setAddAnchor(null)}
                        >
                            <MenuItem onClick={() => handleAdd()}>
                                <FormattedMessage id="finding.add.description" />
                            </MenuItem>
                            <MenuItem onClick={() => handleAdd('structure')}>
                                <FormattedMessage id="finding.add.structure" />
                            </MenuItem>
                        </Menu>
                    </>
                )}
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                >
                    <SearchField
                        size="medium"
                        fullWidth
                        value={search}
                        onChange={(event) =>
                            handleSearchChange(event.target.value)
                        }
                        onClear={() => handleSearchChange('')}
                        placeholder={intl.formatMessage({ id: 'grid.search' })}
                        aria-label={intl.formatMessage({ id: 'grid.search' })}
                    />
                    <FieldSortButton
                        options={SORT_OPTIONS}
                        sort={listSort}
                        onSort={setListSort}
                    />
                </Stack>
            </Stack>
            <DataGridPro
                apiRef={apiRef}
                rows={rows}
                columns={COLUMNS}
                listView
                listViewColumn={listViewColumn}
                filterModel={filterModel}
                sortModel={sortModel}
                getRowHeight={AUTO_ROW_HEIGHT}
                getEstimatedRowHeight={getEstimatedRowHeight}
                disableRowSelectionOnClick
                columnHeaderHeight={0}
                hideFooter
                slots={slots}
                sx={{
                    /**
                     * The card is the row's whole appearance: no grid border,
                     * header, separators or focus ring. The cell's bottom
                     * padding is the gap between cards.
                     */
                    border: 0,
                    /**
                     * The grid's own paper-white surface would sit as a slab
                     * under the cards instead of the page background.
                     */
                    backgroundColor: 'transparent',
                    '& .MuiDataGrid-columnHeaders': { display: 'none' },
                    '& .MuiDataGrid-cell': {
                        padding: 0,
                        paddingBottom: `${CARD_GAP}px`,
                        // The grid's row separator (a top border, not a
                        // bottom one).
                        borderTop: 0,
                        '&:focus, &:focus-within': { outline: 'none' }
                    },
                    '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered':
                        {
                            backgroundColor: 'transparent'
                        }
                }}
            />
            {/* The dialog routes render here, fullscreen over the list. */}
            <Outlet />
        </Box>
    );
}
